import { BaseDataDefinition } from "./BaseDataDefinition";
import { DataTypes } from "../DataTypes";
import { ValidationIssue } from "../../IDesignValidationProvider";
import { JSONDummyData } from "../Constants/DataDefinitionConstants";
import type { AppGlueAny } from "../../AppGlueAny";

export class StringDataDefinition extends BaseDataDefinition<string> {
    readonly type: DataTypes = DataTypes.STRING;

    validValues: string[] = [];
    pattern?: string; // regex format for validation

    static fromJSONObjectValue(value: AppGlueAny, name: string): StringDataDefinition | undefined {
        if (Array.isArray(value)) {
            if (!value.find((v) => typeof v !== "string")) {
                let def = new StringDataDefinition(name);
                def.list = true;

                return def;
            }
        } else if (typeof value === "string") {
            let def = new StringDataDefinition(name);
            return def;
        }

        return undefined;
    }

    static fromJSONSchema(obj: object, name?: string): StringDataDefinition | undefined {
        let def = new StringDataDefinition(name);

        let type = Reflect.get(obj, "type");
        if (type === "array") {
            def.list = true;
            let items = Reflect.get(obj, "items");

            type = Reflect.get(items, "type");

            let pattern = Reflect.get(items, "pattern");
            if (pattern) def.pattern = pattern;

            if (type === "string") {
                return def;
            }
        } else if (type === "string") {
            let pattern = Reflect.get(obj, "pattern");
            if (pattern) def.pattern = pattern;
            return def;
        }

        return undefined;
    }

    getStorageData(): object {
        let object: object = super.getStorageData();

        Reflect.set(object, "validValues", this.validValues);
        Reflect.set(object, "pattern", this.pattern);
        return object;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        this.validValues = Reflect.get(data, "validValues");
        this.pattern = Reflect.get(data, "pattern");
    }

    verifyValueType(value: AppGlueAny): ValidationIssue | undefined {
        if (value === undefined) return new ValidationIssue("Undefined Value");

        if (this.list) {
            // check is array
            if (!Array.isArray(value)) return new ValidationIssue(value + " is not a string array");

            // check each value is of right type
            for (let n of value) {
                if (typeof n !== "string") return new ValidationIssue(value + " has array members that are not string");
            }
        } else {
            if (Array.isArray(value)) return new ValidationIssue(value + " is a string array");

            if (typeof value !== "string") return new ValidationIssue(value + " is not a string");
        }

        if (this.list) {
            if (this.validValues.length > 0) {
                for (let n of value) {
                    if (!this.validValues.includes(n as string)) {
                        return new ValidationIssue(n + " is invalid");
                    }
                }
            }
        } else {
            if (this.validValues.length > 0 && !this.validValues.includes(value as string)) {
                return new ValidationIssue(value + " is invalid");
            }
        }

        return undefined;
    }

    convert(value: AppGlueAny): AppGlueAny {
        if (value === undefined) return undefined;

        if (this.list) {
            if (Array.isArray(value)) {
                let output: any[] = [];

                for (let n of value) {
                    output.push(n.toString());
                }

                return output;
            }
            return value;
        } else {
            return value;
        }
    }

    toJSONObject(value: AppGlueAny): AppGlueAny {
        let issue = this.verifyValueType(value);

        if (issue) {
            value = JSONDummyData.STRING;

            if (this.list) {
                value = [value];
            }
        }

        return value;
    }

    toJSONSchema(): object {
        if (this.list) {
            let obj = {
                type: "array",
                items: { type: this.type },
            };

            if (this.pattern) {
                Reflect.set(obj.items, "pattern", this.pattern);
            }

            return obj;
        }

        let obj = {
            type: this.type,
        };

        if (this.pattern) {
            Reflect.set(obj, "pattern", this.pattern);
        }

        return obj;
    }
}
